import { AlertTriangle, Edit, X } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Role as RoleEnum } from '@ankor-io/common/types/account'

import { useUserContext } from '@/providers/user/useUserContext'
import { Button } from '@/ui-components/Button'
import { Dialog, DialogActions, DialogBody, DialogTitle } from '@/ui-components/Dialog'
import { Select } from '@/ui-components/Select'
import type { OrganizationUser } from '@/views/Users/types'

type EditRoleModalProps = {
  isOpen: boolean
  onClose: () => void
  user: OrganizationUser | null
  onSubmit: (userId: string, updatedRole: RoleEnum) => void
}

const EditRoleModal = ({ isOpen, onClose, user, onSubmit }: EditRoleModalProps) => {
  const { user: currentUser } = useUserContext()
  const [isSaving, setIsSaving] = useState(false)
  const [formData, setFormData] = useState({
    role: user?.role || '',
  })

  // Update form data when user changes
  useEffect(() => {
    if (user) {
      setFormData({
        role: user.role,
      })
    }
  }, [user])

  const handleSubmit = () => {
    if (!user) {
      return
    }

    setIsSaving(true)
    onSubmit(user.id, formData.role as RoleEnum)
  }

  const handleCancel = () => {
    if (user) {
      resetForm()
    }

    setIsSaving(false)
    onClose()
  }

  const resetForm = () => {
    setFormData({
      role: user?.role || '',
    })
  }

  if (!user) {
    return null
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleCancel}
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Edit className="size-5 text-gray-900 dark:text-white" />
          <DialogTitle>Edit User Role</DialogTitle>
        </div>
        <Button
          plain
          onClick={handleCancel}
        >
          <X data-slot="icon" />
        </Button>
      </div>

      <DialogBody>
        <div className="space-y-4">
          {user.id === currentUser?.id && (
            <div className="flex gap-2 text-red-500 dark:text-red-400 text-sm">
              <AlertTriangle className="mt-0.5 size-4 flex-shrink-0" />
              <span>Warning: You are editing your own role. This will affect your access to the organization.</span>
            </div>
          )}
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">Role</label>
            <Select
              value={formData.role}
              onChange={(e) => setFormData((prev) => ({ ...prev, role: e.target.value }))}
              className="w-full"
            >
              <option
                value=""
                disabled
              >
                Select a role
              </option>
              {Object.values(RoleEnum).map((role) => (
                <option
                  key={role}
                  value={role}
                >
                  {role.charAt(0).toUpperCase() + role.slice(1)}
                </option>
              ))}
            </Select>
          </div>
        </div>
      </DialogBody>

      <DialogActions>
        <Button
          plain
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          className="data-disabled:opacity-50 data-disabled:cursor-not-allowed"
          disabled={formData.role === user?.role || isSaving}
          onClick={handleSubmit}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EditRoleModal
